<div class="contact-form-container">
  <!-- Loading Overlay -->
  <div class="loading-overlay" *ngIf="isSubmitting">
    <div class="spinner-container">
      <div class="spinner">
        <div class="spinner-circle"></div>
        <div class="spinner-circle"></div>
        <div class="spinner-circle"></div>
      </div>
      <p class="loading-text">Sending your message...</p>
      <p class="loading-subtext">Please wait while we process your request</p>
    </div>
  </div>

  <div class="form-header text-center mb-4">
    <h2 class="form-title mt-primary-text">Send us a message</h2>
    <p class="form-subtitle">We'll get back to you within 24 hours</p>
  </div>

  <!-- Error Message Display -->
  <div *ngIf="errorMessage && !isSubmitting" class="error-message-box">
    <div class="error-content">
      <div class="error-icon">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="currentColor"/>
        </svg>
      </div>
      <div class="error-text">
        <h4>Submission Failed</h4>
        <p>{{ errorMessage }}</p>
      </div>
      <button class="error-close" (click)="clearError()" type="button">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>
    </div>
  </div>

  <div *ngIf="submitted" class="success-message">
    <div class="success-icon">✅</div>
    <h3>Thank you for your message!</h3>
    <p>We've received your inquiry and will get back to you within 24 hours. In the meantime, feel free to explore our <a routerLink="/pricing">pricing plans</a> or <a routerLink="/">learn more about MemberTrace</a>.</p>
  </div>

  <form *ngIf="!submitted" data-clarity-mask="true" [formGroup]="contactForm" (ngSubmit)="onSubmit()" class="contact-form">
    <div class="row">
      <div class="col-md-6 mb-3">
        <mat-form-field appearance="outline" class="w-100">
          <label matLabel for="contact-first-name">First Name</label>
          <input matInput id="contact-first-name" formControlName="firstName" placeholder="Enter your first name">
          <mat-error *ngIf="contactForm.get('firstName')?.invalid && contactForm.get('firstName')?.touched">
            {{ getErrorMessage('firstName') }}
          </mat-error>
        </mat-form-field>
      </div>
      
      <div class="col-md-6 mb-3">
        <mat-form-field appearance="outline" class="w-100">
          <label matLabel for="contact-last-name">Last Name</label>
          <input matInput id="contact-last-name" formControlName="lastName" placeholder="Enter your last name">
          <mat-error *ngIf="contactForm.get('lastName')?.invalid && contactForm.get('lastName')?.touched">
            {{ getErrorMessage('lastName') }}
          </mat-error>
        </mat-form-field>
      </div>
    </div>

    <div class="row">
      <div class="col-md-6 mb-3">
        <mat-form-field appearance="outline" class="w-100">
          <label matLabel for="contact-email">Email Address</label>
          <input matInput id="contact-email" formControlName="email" type="email" placeholder="Enter your email address">
          <mat-error *ngIf="contactForm.get('email')?.invalid && contactForm.get('email')?.touched">
            {{ getErrorMessage('email') }}
          </mat-error>
        </mat-form-field>
      </div>
      
      <div class="col-md-6 mb-3">
        <mat-form-field appearance="outline" class="w-100">
          <label matLabel for="contact-phone">Phone Number</label>
          <input matInput id="contact-phone" formControlName="phone" placeholder="Enter your phone number (optional)">
        </mat-form-field>
      </div>
    </div>

    <div class="row">
      <div class="col-md-6 mb-3">
        <mat-form-field appearance="outline" class="w-100">
          <label matLabel for="contact-organization">Organization</label>
          <input matInput id="contact-organization" formControlName="organization" placeholder="Enter your organization name">
          <mat-error *ngIf="contactForm.get('organization')?.invalid && contactForm.get('organization')?.touched">
            {{ getErrorMessage('organization') }}
          </mat-error>
        </mat-form-field>
      </div>
      
      <div class="col-md-6 mb-3">
        <mat-form-field appearance="outline" class="w-100">
          <mat-label>Inquiry Type</mat-label>
          <mat-select formControlName="inquiryType">
            <mat-option value="general">General Inquiry</mat-option>
            <mat-option value="demo">Request Demo</mat-option>
            <mat-option value="operator">AMC / operator workflow</mat-option>
            <mat-option value="pricing">Pricing Questions</mat-option>
            <mat-option value="managed-support">Managed Support</mat-option>
            <mat-option value="launch-program">Launch Program</mat-option>
            <mat-option value="migration">Concierge Migration</mat-option>
            <mat-option value="support">Technical Support</mat-option>
            <mat-option value="partnership">Partnership</mat-option>
            <mat-option value="enterprise">Enterprise &amp; custom mobile apps</mat-option>
          </mat-select>
        </mat-form-field>
      </div>
    </div>

    <div class="mb-4">
      <mat-form-field appearance="outline" class="w-100">
        <label matLabel for="contact-message">Message</label>
        <textarea matInput id="contact-message" formControlName="message" rows="5" placeholder="Tell us about your needs and how we can help..."></textarea>
        <mat-hint align="end">
          <span [class.message-count-invalid]="messageLength < messageMinLength">{{ messageLength }} / {{ messageMinLength }} characters minimum</span>
        </mat-hint>
        <mat-error *ngIf="contactForm.get('message')?.invalid && contactForm.get('message')?.touched">
          {{ getErrorMessage('message') }}
        </mat-error>
      </mat-form-field>
    </div>

    <!-- Turnstile widget at bottom of form (visible, like most contact forms) -->
    <div class="turnstile-wrapper">
      <div *ngIf="turnstileLoadError" class="turnstile-message turnstile-error">
        <span>Verification couldn't load. Check your connection or VPN, then try again.</span>
        <button mat-button type="button" color="primary" (click)="retryTurnstile()" class="turnstile-retry">
          Retry verification
        </button>
      </div>
      <div *ngIf="!turnstileReady && !turnstileLoadError" class="turnstile-message turnstile-loading">
        Loading verification…
      </div>
      <div id="turnstile-container"></div>
      <p *ngIf="turnstileReady && !turnstileVerified && !turnstileLoadError" class="turnstile-hint">
        Please complete the verification above before sending.
      </p>
    </div>

    <div class="text-center">
      <button 
        mat-flat-button 
        color="accent" 
        type="submit" 
        [disabled]="isSubmitting || contactForm.invalid || !turnstileReady || !turnstileVerified || turnstileLoadError"
        class="submit-button">
        Send Message
      </button>
    </div>
  </form>
</div>
